/* eslint-disable */
// src/tables/buildVisitTables.js

import React from "react";
import { getColumns } from "utils/tableSchemas";
// Usamos los formateadores dinámicos como defaults
import { nfDynamic as defaultNf, moneyFmtDynamic as defaultMoneyFmt } from "utils/formatters";
import { buildMachineDisplayName } from "utils/machineDisplayName";
import { formatFormulaLabel } from "utils/formula";
import * as VS from "./visitStrategies/strategySelector";

/* File: src/tables/buildVisitTables.js (UI Strategy)
 * Devuelve { columns, rows } delegando a estrategias por tipo/fila.
 */

export default function buildVisitTable({ mode, type, items = [], helpers = {}, options = {} }) {
  const {
    nf = defaultNf,
    moneyFmt = defaultMoneyFmt,
    getLastVisit = () => ({}),
    computeKey = (m) => m?.LocalMachineId ?? m?.Id,
    userInputs = {},
    settledInputFields = {},
    handleInputChange = () => {},
    handleToggleReset = () => {},
    wasCollectedByLM = {},
    handleToggleWasCollected = () => {},
    counterVerificationContextsByLocalMachineId = {},
    isCounterVerificationExpanded = () => false,
    toggleCounterVerificationExpanded = () => {},
    lastGlobalVisitId = null,
  } = helpers;

  const compactFlag = Boolean(options.compact);
  const individualEditorLayout = Boolean(options.individualEditorLayout);
  const collectionDetailPresentation = Boolean(options.collectionDetailPresentation);

  // === Estilos/formatos UI ===
  const rowLine = { display: "flex", alignItems: "center", gap: 8 };
  const inputStyle = {
    width: individualEditorLayout ? "100%" : compactFlag ? 72 : 80,
    height: individualEditorLayout ? 48 : undefined,
    boxSizing: individualEditorLayout ? "border-box" : undefined,
    fontSize: individualEditorLayout ? 18 : undefined,
    textAlign: "right",
    border: "1px solid #ccc",
    borderRadius: 4,
    padding: individualEditorLayout ? "10px 12px" : 4,
  };
  const textInputStyle = { ...inputStyle, width: "100%" };

  // En recaudaciones los inputs editables son enteros (sin decimales).
  const numericProps = {
    displayType: "input",
    thousandSeparator: ".",
    decimalSeparator: ",",
    decimalScale: 0,
    isAllowed: ({ value }) => /^\d*$/.test(value),
    allowNegative: false,
    allowLeadingZeros: false,
    style: inputStyle,
  };

  // usamos let para poder ajustar encabezados luego
  let columns = getColumns(type, {
    mode,
    hideTotal: Boolean(options.hideTotal),
    compact: compactFlag,
  });

  // helper de accesibilidad
  const makeLabel = (m, field) => {
    const name = buildMachineDisplayName(m, {
      fallback: `Máquina ${m.LocalMachineId ?? m.Id ?? ""}`,
    });
    return `${field} — ${name}`;
  };

  // helpers fórmula — nombre y valor
  const getFormulaName = (vm) => {
    const t = String(type || vm?.MachineType || vm?.MachineType?.Type || "").toLowerCase();
    const isTraga = t.includes("tragamoneda") || t.includes("tragamonedas");
    return formatFormulaLabel(vm, { fallback: isTraga ? "sin fórmula" : "" });
  };

  const getFormulaValue = (vm) => {
    const raw =
      vm?.FormulaValue ??
      vm?.Formula?.Value ??
      vm?.LocalMachine?.Formula?.Value ??
      vm?.LocalMachine?.MachineFormula?.Value ??
      vm?.Machine?.Formula?.Value ??
      vm?.Machine?.MachineFormula?.Value ??
      null;
    const num = raw != null ? Number(raw) : null;
    return Number.isFinite(num) ? num : null;
  };

  // ====== Contexto para estrategias ====== (se usa en las filas)
  const ctx = {
    mode,
    type,
    helpers: {
      nf,
      moneyFmt,
      getLastVisit,
      computeKey,
      userInputs,
      settledInputFields,
      handleInputChange,
      handleToggleReset,
      wasCollectedByLM,
      handleToggleWasCollected,
      counterVerificationContextsByLocalMachineId,
      isCounterVerificationExpanded,
      toggleCounterVerificationExpanded,
      lastGlobalVisitId,
      makeLabel,
      getFormulaName,
      getFormulaValue,
    },
    ui: { rowLine, inputStyle, textInputStyle, numericProps },
    options: {
      compact: compactFlag,
      hideTotal: Boolean(options.hideTotal),
      individualEditorLayout,
      collectionDetailPresentation,
    },
  };

  // === OVERRIDE DE ENCABEZADOS (p.ej., { In: "Entrada" }) ===
  if (options.columnTitles && Array.isArray(columns)) {
    const overrides = options.columnTitles;
    columns = columns.map((col) => {
      const accessor = col.accessor ?? col.field ?? col.key ?? col.id ?? null;
      const currentLabel = col.Header ?? col.name ?? col.title ?? col.label ?? null;

      const newTitle =
        (accessor && overrides[accessor]) || (currentLabel && overrides[currentLabel]) || null;

      if (!newTitle) return col;

      return {
        ...col,
        Header: newTitle,
        header: newTitle,
        name: newTitle,
        title: newTitle,
        label: newTitle,
      };
    });
  }

  // === AÑADIR COLUMNA "Nota" EN DETALLE (modo read) SI NO EXISTE ===
  if (mode === "read" && Array.isArray(columns)) {
    const hasNote = columns.some((c) => {
      const key = (c.accessor ?? c.field ?? c.key ?? c.id ?? "").toString().toLowerCase();
      const label = (c.Header ?? c.name ?? c.title ?? c.label ?? "").toString().toLowerCase();
      return key === "note" || label === "nota" || label === "note";
    });

    if (!hasNote) {
      const totalColumn = columns.find((c) => (c.accessor ?? c.field ?? c.key ?? c.id) === "Total");
      const noteWidth =
        type === "Tragamoneda" && totalColumn
          ? {
              width: totalColumn.width,
              minWidth: totalColumn.minWidth,
              maxWidth: totalColumn.maxWidth,
            }
          : { width: "auto" };
      columns = [
        ...columns,
        {
          Header: "Nota",
          accessor: "Note",
          align: "left",
          ...noteWidth,
        },
      ];
    }
  }

  // Cache de instancias por clase para no reinstanciar en cada fila
  const instanceCache = new Map();
  let warnedOnce = false;

  const rows = items.flatMap((m, sourceItemIndex) => {
    // Elegimos estrategia por FILA: si está alquilada => RentStrategy
    // Fallbacks defensivos por si el módulo no cargó aún (HMR/caché)
    const pickForItem =
      typeof VS.getVisitTableStrategyForItem === "function"
        ? VS.getVisitTableStrategyForItem
        : null;
    const pickByType =
      typeof VS.getVisitTableStrategy === "function" ? VS.getVisitTableStrategy : null;

    if (!pickForItem || !pickByType) {
      if (!warnedOnce && process.env.NODE_ENV !== "production") {
        console.warn(
          "[buildVisitTable] selector de estrategias no disponible; usando fallback por tipo.",
          { hasForItem: !!pickForItem, hasByType: !!pickByType }
        );
        warnedOnce = true;
      }
    }

    const StrategyCtor = pickForItem?.(m, type, mode) || pickByType?.(type);
    if (!StrategyCtor) {
      throw new Error("No se pudo resolver StrategyCtor (selector de estrategias).");
    }

    let strategy = instanceCache.get(StrategyCtor);
    if (!strategy) {
      strategy = new StrategyCtor(ctx);
      instanceCache.set(StrategyCtor, strategy);
    }
    const built = strategy.buildCells(m);
    const builtRows = Array.isArray(built) ? built : [built];
    const sourceLocalMachineId = m?.LocalMachineId ?? m?.localMachineId ?? m?.Id ?? m?.id ?? null;
    return builtRows.map((row, sourceRowIndex) => ({
      ...row,
      __sourceItemIndex: sourceItemIndex,
      __sourceLocalMachineId: sourceLocalMachineId,
      __sourceRowIndex: sourceRowIndex,
      __isAdditionalSourceRow: sourceRowIndex > 0,
      __isCounterVerificationDetailRow: row?.__isCounterVerificationDetailRow === true,
    }));
  });

  return { columns, rows };
}
